import { useMemo, useRef, useState } from 'react';
import { fmt, wD, todayISO } from '../../lib/format.js';
import { cat, cats } from '../../domain/categories.js';
import { keyAdd, keyOfISO, periodLabel, curKey } from '../../domain/periods.js';
import { scanCoverage } from '../../domain/coverage.js';
import { recForScanItem } from '../../domain/recurring.js';
import { Seg, Sheet, confirmDialog } from '../../components/ui.jsx';
import { Icon } from '../../components/Icon.jsx';
import { openModal, closeModal } from '../../app/nav.js';
import { toast, useApp } from '../../store/ui.js';
import { insertTx, deleteTx } from '../../store/actions.js';
import { analyzeFile, fileKind, previewPlan, saveScan, applyBankBalance } from '../../store/scan.js';

export function ScanPanel() {
  useApp();
  const [src, setSrc] = useState('cash');
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState('');
  const [busy, setBusy] = useState('');
  const [err, setErr] = useState('');
  const [res, setRes] = useState(null); // { items, meta }
  const [period, setPeriod] = useState('');
  const [filter, setFilter] = useState('all');
  const cam = useRef(null), gal = useRef(null);

  function reset() {
    setFile(null); setPreview(''); setErr(''); setRes(null); setFilter('all'); setPeriod('');
    if (cam.current) cam.current.value = '';
    if (gal.current) gal.current.value = '';
  }
  const [over, setOver] = useState(false);
  function pick(e) {
    const f = e.target.files ? e.target.files[0] : e.dataTransfer && e.dataTransfer.files[0];
    if (!f) return;
    reset();
    setFile(f);
    if (fileKind(f) === 'image') { const r = new FileReader(); r.onload = (ev) => setPreview(ev.target.result); r.readAsDataURL(f); }
  }
  async function analyze() {
    setErr('');
    setBusy('מנתח...');
    try {
      const out = await analyzeFile(file, setBusy);
      // סוג הדוח מזוהה מהקובץ: דוח כרטיס נשמר כאשראי, קובץ עו"ש כעו"ש
      const det = out.meta.detectedSrc;
      if (det && det !== src) { setSrc(det); toast(det === 'credit' ? '💳 זוהה דוח כרטיס אשראי. נשמר כאשראי' : '🏦 זוהה קובץ עו"ש. נשמר כעו"ש'); }
      // דף אשראי הוא מחזור חיוב אחד: כל השורות שייכות לתקופת החיוב, לא לתאריך הרכישה
      setPeriod(out.meta.chargeDate ? keyOfISO(out.meta.chargeDate) : '');
      setRes(out);
    } catch (e) {
      setErr(e.message);
    }
    setBusy('');
  }
  async function save() {
    setBusy('שומר...');
    try {
      const r = await saveScan(res.items, src, period, res.meta);
      reset();
      if (r.near.length || r.missing.length) openModal(() => <ScanReport r={r} src={src} />);
      else {
        let msg = 'נשמרו ' + r.saved + ' חדשות' + (r.matched ? ' · ' + r.matched + ' כבר היו קיימות ולא נשמרו שוב' : '');
        if (r.updated) msg += ' · ' + r.updated + ' סכומים עודכנו לפי הדוח';
        if (r.infile) msg += ' · ' + r.infile + ' כפולות בתוך הדוח דולגו';
        toast(r.failed ? '⚠️ ' + r.failed + ' פעולות לא נשמרו (' + r.firstErr + '). סרוק שוב את הקובץ: מה שכבר נשמר לא יוכפל · ' + msg : '✅ ' + msg);
      }
    } catch (e) {
      toast('⚠️ ' + e.message);
    }
    setBusy('');
  }

  return (
    <div>
      <div className="mb12"><label className="lbl">סוג הדוח</label><Seg value={src} onChange={setSrc} options={[['cash', '🏦 עו"ש'], ['credit', '💳 כרטיס אשראי']]} /></div>
      <Coverage src={src} />
      <input ref={cam} type="file" accept="image/*" capture="environment" onChange={pick} style={{ display: 'none' }} />
      <input ref={gal} type="file" accept="image/*,application/pdf,.xlsx,.xls,.csv" onChange={pick} style={{ display: 'none' }} />
      {!res && !file && (
        <div className="mb12">
          <button className={'drop' + (over ? ' over' : '')} onClick={() => gal.current.click()}
            onDragOver={(e) => { e.preventDefault(); setOver(true); }} onDragLeave={() => setOver(false)}
            onDrop={(e) => { e.preventDefault(); setOver(false); pick(e); }}>
            <Icon name="upload" size={26} />
            <span>גרור לכאן קובץ, או לחץ לבחירה</span>
            <small>Excel, CSV, PDF או תמונה</small>
          </button>
          <button className="ubtn ubtn-outline w100 mt10 mobile-only" onClick={() => cam.current.click()}><Icon name="camera" size={17} />צילום דף מהדוח</button>
        </div>
      )}
      {file && !res && (
        <div className="mb12">
          {preview ? <img src={preview} alt="" style={{ width: '100%', borderRadius: 8, maxHeight: 160, objectFit: 'contain', background: '#111' }} />
            : <div className="box2 note" style={{ textAlign: 'center' }}>{fileKind(file) === 'excel' ? '📊' : '📄'} {file.name} ({Math.round(file.size / 1024)}KB)</div>}
        </div>
      )}
      {err && <div className="err-box" style={{ display: 'block', marginBottom: 10 }}>שגיאה: {err}</div>}
      {res && <ScanResults res={res} src={src} period={period} setPeriod={setPeriod} filter={filter} setFilter={setFilter}
        onItem={(i, patch) => setRes((r) => ({ ...r, items: r.items.map((x, j) => (j === i ? { ...x, ...patch } : x)) }))} />}
      <div className="row">
        {file && !res && <button className="ubtn ubtn-primary" style={{ flex: 1 }} disabled={!!busy} onClick={analyze}>{busy ? <><span className="sp" /> {busy}</> : err ? '✨ נסה שוב' : '✨ נתח'}</button>}
        {res && <SaveButton res={res} src={src} period={period} busy={busy} onSave={save} />}
        {(file || res) && <button className="ubtn ubtn-outline" onClick={reset}>ביטול</button>}
      </div>
    </div>
  );
}

function SaveButton({ res, src, period, busy, onSave }) {
  const p = previewPlan(res.items, src, period);
  const n = p.st.filter((s) => s.status === 'new').length, near = p.st.filter((s) => s.status === 'near').length;
  return (
    <button className="ubtn" style={{ flex: 1, background: 'var(--green)', color: '#fff' }} disabled={!!busy} onClick={onSave}>
      {busy ? <><span className="sp" /> {busy}</> : n ? '✓ שמור ' + n + ' חדשות' : near ? '✓ המשך לבדיקה' : '✓ סיים (הכל כבר קיים)'}
    </button>
  );
}

/** עד מתי כל כרטיס וחשבון נסרק */
function Coverage({ src }) {
  const cov = scanCoverage();
  if (!cov.length) return null;
  const now = new Date(todayISO() + 'T12:00:00');
  return (
    <div className="box2 mb12">
      <div style={{ fontSize: '.82rem', fontWeight: 800, color: 'var(--ink2)', marginBottom: 5 }}>📅 נסרק עד</div>
      {cov.map((c) => {
        const ago = Math.round((now - new Date(c.date + 'T12:00:00')) / 86400000);
        const col = ago >= 10 ? 'var(--red)' : ago >= 5 ? 'var(--gold)' : 'var(--green)';
        return (
          <div key={c.key} className="row-b" style={{ padding: '4px 0', fontSize: '.82rem', opacity: c.src === src ? 1 : 0.6 }}>
            <span style={{ fontWeight: c.src === src ? 700 : 500 }}>{c.label}</span>
            <span className="nowrap"><b style={{ color: col }}>{wD(c.date)}</b><span className="muted"> · {ago === 0 ? 'היום' : 'לפני ' + ago + ' ימים'}</span></span>
          </div>
        );
      })}
      <div className="note mt6">הורד את הדוח הבא מהתאריך הזה והלאה. יום החפיפה לא ייצור כפילות.{cov.some((c) => c.approx) ? ' בעו"ש התאריך כולל גם פעולות שהוזנו ידנית, עד הסריקה הבאה.' : ''}</div>
    </div>
  );
}

const BADGE = {
  new: ['חדש', { background: 'var(--green-l)', color: 'var(--green)' }],
  dup: ['קיים', { background: 'var(--surface2)', color: 'var(--ink3)' }],
  infile: ['כפולה בדוח', { background: 'var(--surface2)', color: 'var(--ink3)' }],
  near: ['לבדיקה', { background: 'var(--gold-l)', color: 'var(--ink2)' }],
  skip: ['לא יישמר', { background: 'var(--surface2)', color: 'var(--ink3)' }],
};

function ScanResults({ res, src, period, setPeriod, filter, setFilter, onItem }) {
  const { items, meta } = res;
  const [open, setOpen] = useState(-1);
  const sameName = (it) => items.filter((x) => x.description === it.description).length;
  // קטגוריה של עסק משתנה בכל השורות שלו בדוח, וזיכרון העסקים לומד אותה מהשמירה
  const onCat = (it, catId) => items.forEach((x, j) => { if (x.description === it.description && (x.type || 'expense') === (it.type || 'expense')) onItem(j, { cat: catId, fromMemory: false }); });
  const plan = useMemo(() => previewPlan(items, src, period), [items, src, period]);
  const n = { new: 0, dup: 0, near: 0, skip: 0, infile: 0 };
  plan.st.forEach((s) => (n[s.status] = (n[s.status] || 0) + 1));
  let charges = 0, refunds = 0, internal = 0;
  items.forEach((i) => {
    if (i.notYetBilled) return; // טרם חויבו, לא חלק מסכום המחזור
    const a = Math.abs(Number(i.amount) || 0);
    if (i.internal) internal += a;
    if (i.type === 'income') refunds += a; else charges += a;
  });
  const total = charges - refunds;
  const pend = items.filter((i) => i.notYetBilled), pendSum = pend.reduce((s, i) => s + Math.abs(Number(i.amount) || 0), 0);
  const settle = items.filter((i) => i.cardSettlement);
  const chip = (k, label, c) => <button key={k} className={'pill' + (filter === k ? ' on' : '')} onClick={() => setFilter(k)}>{label} {c}</button>;
  return (
    <div className="mb12" style={{ fontSize: '.9rem' }}>
      <div className="box2 mb12">
        <div style={{ fontSize: '.9rem', fontWeight: 800, marginBottom: 8 }}>
          {n.new ? '🆕 ' + n.new + ' יישמרו' : '✓ אין עסקאות חדשות'}{n.dup ? ' · ' + n.dup + ' כבר קיימות' : ''}{n.near ? ' · ' + n.near + ' לבדיקה' : ''}{n.infile ? ' · ' + n.infile + ' כפולות בדוח' : ''}
        </div>
        <div className="row" style={{ gap: 6, flexWrap: 'wrap' }}>
          {chip('all', 'הכל', plan.st.length)}{chip('new', 'חדשות', n.new)}{n.near ? chip('near', 'לבדיקה', n.near) : null}{chip('dup', 'קיימות', n.dup)}{n.infile ? chip('infile', 'כפולות בדוח', n.infile) : null}
        </div>
        {n.near > 0 && <div className="note mt6">"לבדיקה" = סכום זהה לפעולה שמורה, בהפרש של עד 3 ימים. אחרי השמירה תבחר אם הן חדשות.</div>}
        {plan.older && <div className="note mt6">🕰️ הדוח הזה ישן יותר ממה שכבר נסרק. יישמרו רק עסקאות שאין עדיין, ולא ייבדקו עסקאות חסרות.</div>}
        {plan.missing.length > 0 && <div className="note mt6">🔍 {plan.missing.length} שמורות מאותו כרטיס לא מופיעות בדוח הזה. יוצגו אחרי השמירה.</div>}
      </div>
      <div className="note mb8">לחיצה על שורה: שינוי קטגוריה, שם, או "לא לשמור".</div>
      {items.map((it, i) => {
        const s = it.skip ? 'skip' : (plan.st[i] || {}).status || 'new';
        if (filter !== 'all' && s !== filter) return null;
        const c = cat(it.type || 'expense', it.cat);
        const rec = recForScanItem(it, src);
        const faded = s === 'dup' || s === 'skip' || s === 'infile';
        const editable = s === 'new' || s === 'near' || s === 'skip';
        return (
          <div key={i} className={'scan-item' + (open === i ? ' open' : '')}>
            <div className={'scan-row' + (editable ? ' clickable' : '')} style={{ opacity: faded && open !== i ? 0.45 : 1 }} onClick={editable ? () => setOpen(open === i ? -1 : i) : undefined}>
              <span className="badge-s" style={BADGE[s][1]}>{BADGE[s][0]}</span>
              <span title={c.label}>{it.internal ? '🔄' : c.icon}</span>
              <span className="grow" style={{ fontSize: '.93rem', textDecoration: it.skip ? 'line-through' : undefined }}>
                {it.description}{it.internal && <span className="muted" style={{ fontSize: '.82rem' }}> פנימי</span>}
                {it.fromMemory && <span title="סווג לפי היסטוריה" className="muted" style={{ fontSize: '.82rem' }}> · {c.label}</span>}
                {it.orig && !/ILS@/.test(it.orig) && <><br /><span style={{ fontSize: '.82rem', color: it.fxSrc === 'default' ? 'var(--red)' : 'var(--ink3)' }}>
                  💱 {it.orig}{it.fxRate ? ' · שער ' + it.fxRate.toFixed(2) + (it.fxSrc === 'sum' ? ' (של ישראכרט)' : it.fxSrc === 'hist' ? ' (מעסקאות קודמות)' : ' (הערכה, לבדיקה)') : ''}</span></>}
                {rec ? <><br /><span style={{ fontSize: '.82rem', color: 'var(--green)' }}>🔁 חיוב קבוע: {rec.description}{Math.abs(Math.abs(rec.amount) - it.amount) >= 0.5 ? ' · מוגדר ' + fmt(rec.amount) + ', בפועל ' + fmt(it.amount) : ''}</span></>
                  : it.standing && <><br /><span style={{ fontSize: '.82rem', color: 'var(--gold)' }}>🔁 הוראת קבע, לא מוגדרת אצלך כחיוב קבוע</span></>}
              </span>
              <span className="note nowrap">{wD(it.date)}</span>
              <span className="nowrap" style={{ fontWeight: 700, fontSize: '.93rem', color: it.type === 'income' ? 'var(--green)' : 'var(--ink)' }}>{fmt(it.amount, it.type === 'income' ? '+' : '−')}</span>
            </div>
            {open === i && (
              <div className="scan-edit">
                <div className="form-row" style={{ marginBottom: 8 }}>
                  <div><label className="lbl">שם</label><input value={it.description} onChange={(e) => onItem(i, { description: e.target.value })} /></div>
                  <div><label className="lbl">קטגוריה</label>
                    <select value={it.cat} onChange={(e) => onCat(it, e.target.value)}>
                      {cats(it.type || 'expense').map((x) => <option key={x.id} value={x.id}>{x.icon} {x.label}</option>)}
                    </select></div>
                </div>
                <div className="row-b">
                  <span className="note">{sameName(it) > 1 ? 'הקטגוריה תשתנה בכל ' + sameName(it) + ' השורות של העסק הזה' : ''}</span>
                  <button className={'ubtn btn-sm ' + (it.skip ? 'ubtn-outline' : 'ubtn-ghost danger-text')} onClick={() => { onItem(i, { skip: !it.skip }); setOpen(-1); }}>{it.skip ? 'כן לשמור' : 'לא לשמור את השורה'}</button>
                </div>
              </div>
            )}
          </div>
        );
      })}
      {src === 'credit' && <>
      <div className="row-b mt10" style={{ paddingTop: 8, borderTop: '2px solid var(--border)', fontWeight: 700 }}>
        <span>{items.length - pend.length} לחיוב{pend.length ? ' · ' + pend.length + ' טרם נקלטו' : ''}</span><span className="red">{fmt(total)}</span>
      </div>
      <div className="note mt6">חיובים {fmt(charges)}{refunds ? ' · זיכויים ' + fmt(refunds) : ''}{internal ? ' · מזוהות כפנימיות ' + fmt(internal) + ' (נספרות בסכום)' : ''}<br />הסכום אמור להיות זהה לשורת "סה"כ לחיוב" בדוח</div>
      {pend.length > 0 && <div className="box2 mt10" style={{ background: 'var(--surface)', fontSize: '.82rem', lineHeight: 1.6 }}>
        <b>🕓 {pend.length} עסקאות שטרם נקלטו · {fmt(pendSum)}</b><br />ישראכרט עדיין לא סגרה אותן ולכן הן אינן בשורת "סה"כ לחיוב", אבל הן יחויבו באותו מועד. החיוב בפועל צפוי להיות {fmt(total + pendSum)}.
      </div>}
      <Verify total={total} />
      </>}
      {settle.length > 0 && <div className="alert-green mt10" style={{ fontSize: '.82rem', lineHeight: 1.6 }}>
        <b>💳 זוהה חיוב כרטיס אשראי: {settle.map((i) => fmt(i.amount)).join(', ')}</b><br />הוא לא ייספר כהוצאה, כי העסקאות שבתוכו כבר נרשמו מדוח הכרטיס. ביתרת העו"ש הוא כן נכנס, כי שם הכסף באמת ירד.
      </div>}
      {meta.bankLatest && meta.bankLatest.balance != null && <div className="alert-green mt10" style={{ fontSize: '.82rem', lineHeight: 1.65 }}>
        <b>🏦 יתרת חשבון בקובץ: {fmt(meta.bankLatest.balance)}</b> נכון ל-{meta.bankLatest.date}<br />
        <button className="ubtn ubtn-outline ubtn-sm-pad mt6" onClick={() => applyBankBalance(meta.bankLatest)}>עדכן נקודת פתיחה לתזרים</button>
      </div>}
      {meta.installments && meta.installments.length > 0 && <div className="alert-gold mt10" style={{ fontSize: '.82rem', lineHeight: 1.6, marginBottom: 0 }}>
        <b>💳 {meta.installments.length} עסקאות בתשלומים שטרם הסתיימו</b><br />
        {meta.installments.map((r, i) => <span key={i}>{r.name}: תשלום {r.instNum}/{r.instTotal}, נותרו {r.instTotal - r.instNum} × {fmt(r.amount)}<br /></span>)}
        <span className="muted">יתווספו לתחזית כחיובים עתידיים בשמירה.</span>
      </div>}
      {meta.mem && (meta.mem.fromMemory || meta.mem.unified || meta.mem.canon) ? <div className="note mt6">● {meta.mem.fromMemory} סווגו לפי היסטוריה{meta.mem.unified ? ', ' + meta.mem.unified + ' אוחדו לעקביות' : ''}{meta.mem.canon ? ', ' + meta.mem.canon + ' שמות אוחדו לאיות אחד' : ''}</div> : null}
      <div className="mt12">
        <label className="lbl">📅 שייך לתקופה</label>
        <select value={period} onChange={(e) => setPeriod(e.target.value)}>
          {meta.chargeDate && <option value={keyOfISO(meta.chargeDate)}>דוח אשראי לחיוב {wD(meta.chargeDate)} (מומלץ)</option>}
          <option value="">לפי תאריך העסקה המקורי</option>
          {[0, 1, 2, 3, 4, 5].map((i) => { const k = keyAdd(curKey(), -i); return <option key={k} value={k}>{periodLabel(k)}{i === 0 ? ' (נוכחי)' : ''}</option>; })}
        </select>
      </div>
    </div>
  );
}

function Verify({ total }) {
  const [v, setV] = useState('');
  const n = parseFloat(String(v).replace(/[^0-9.-]/g, ''));
  const diff = isNaN(n) ? null : Math.round((n - total) * 100) / 100;
  return (
    <>
      <div className="row mt10" style={{ gap: 7 }}>
        <input type="number" inputMode="decimal" placeholder="הסכום מהדוח" className="inline-input" value={v} onChange={(e) => setV(e.target.value)} style={{ flex: 1 }} />
      </div>
      {diff != null && <div style={{ fontSize: '.82rem', marginTop: 6, lineHeight: 1.5 }}>
        {Math.abs(diff) < 0.02 ? <b className="green">✓ תואם במדויק</b> : <>
          <b className="red">✗ פער של {fmt(Math.abs(diff))}</b><br />
          <span className="muted">{diff > 0 ? 'חסרות שורות בסריקה' : 'נספרו שורות עודפות'}. בדוק שורות סיכום, זיכויים ועסקאות במטבע זר.</span></>}
      </div>}
    </>
  );
}

/** אחרי שמירה: "לבדיקה" (דומות לקיימות) ושמורות שלא הופיעו בדוח */
function ScanReport({ r, src }) {
  const [sel, setSel] = useState(() => r.near.map(() => false));
  const [missing, setMissing] = useState(r.missing);
  async function saveSelected() {
    let n = 0;
    for (let i = 0; i < r.near.length; i++) {
      if (!sel[i]) continue;
      const it = r.near[i].item;
      const body = { description: it.description, amount: it.amount, type: it.type || 'expense', cat: it.cat || 'other', date: it.date || todayISO(), month: it.month, source: src };
      if (it.internal || it.cardSettlement) body.excluded = true;
      try { await insertTx(body, it); n++; } catch (e) { toast('⚠️ לא נשמר: ' + e.message); }
    }
    closeModal();
    if (n) toast('✅ נשמרו ' + n + ' פעולות נוספות');
  }
  async function delMissing(t) {
    if (!(await confirmDialog({ title: 'מחיקת פעולה', body: '"' + t.description + '" ' + fmt(t.amount), okLabel: 'מחק', danger: true }))) return;
    try { await deleteTx(t.id); setMissing(missing.filter((x) => x.id !== t.id)); toast('🗑️ נמחק'); } catch (e) { toast('שגיאה: ' + e.message); }
  }
  return (
    <Sheet title="דוח ייבוא">
      <div style={{ fontSize: '.9rem', color: 'var(--green)', fontWeight: 700, marginBottom: 6 }}>✅ נשמרו {r.saved} פעולות חדשות</div>
      {r.matched > 0 && <div className="sub">{r.matched} כבר היו קיימות ולא נשמרו שוב</div>}
      {r.infile > 0 && <div className="sub">{r.infile} הופיעו בדוח פעמיים, ונשמרו פעם אחת</div>}
      {r.updated > 0 && <div className="sub">{r.updated} סכומים עודכנו לפי הדוח החדש</div>}
      {r.failed > 0 && <div className="sub red">⚠️ {r.failed} לא נשמרו ({r.firstErr}). סרוק שוב: מה שכבר נשמר לא יוכפל</div>}
      {missing.length > 0 && (
        <div className="mt12">
          <div style={{ fontSize: '.9rem', fontWeight: 700 }}>🔍 {missing.length} שמורות שלא מופיעות בדוח החדש</div>
          <div className="note mb8">הן בטווח התאריכים של הדוח ומאותו כרטיס או חשבון. כנראה בוטלו או שהסכום שלהן השתנה.</div>
          {missing.map((t) => (
            <div key={t.id} className="row" style={{ padding: '6px 0', borderBottom: '1px solid var(--border)', fontSize: '.86rem' }}>
              <span className="grow ellipsis">{t.description}</span><span className="muted nowrap">{String(t.date).slice(0, 10)}</span>
              <b className="nowrap">{fmt(t.amount)}</b>
              <button className="sheet-x" style={{ color: 'var(--red)', fontSize: '1rem' }} onClick={() => delMissing(t)} aria-label="מחק">×</button>
            </div>
          ))}
        </div>
      )}
      {r.near.length > 0 && (
        <div className="mt12">
          <div style={{ fontSize: '.9rem', fontWeight: 700, marginBottom: 8 }}>⚠️ {r.near.length} פעולות דומות לקיימות. סמן רק את מה שבאמת חדש:</div>
          {r.near.map((d, i) => (
            <label key={i} className="row" style={{ padding: '6px 0', borderBottom: '1px solid var(--border)', fontSize: '.86rem', cursor: 'pointer' }}>
              <input type="checkbox" checked={sel[i]} onChange={(e) => setSel(sel.map((x, j) => (j === i ? e.target.checked : x)))} style={{ width: 'auto', minHeight: 0 }} />
              <span className="grow ellipsis">{d.item.description}</span>
              <span className="nowrap red" style={{ fontWeight: 700 }}>{fmt(d.item.amount)}</span>
              <span className="nowrap muted">קיימת {String(d.existing.date).slice(0, 10)}</span>
            </label>
          ))}
          <button className="ubtn ubtn-primary w100 mt12" onClick={saveSelected}>הכנס נבחרים</button>
        </div>
      )}
      <button className="ubtn ubtn-ghost w100 mt10" onClick={closeModal}>סגור</button>
    </Sheet>
  );
}

